/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import adminBillingOverviewQuery from 'admin/queries/adminBillingOverviewQuery.gql';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import BoxBody from 'components/BoxBody';
import Config from 'Config';
import Dialog from 'components/Dialog';
import Field from 'components/Field';
import ToastActions from 'actions/ToastActions';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import LinkConstants from 'constants/LinkConstants';
import CreditCardInput from 'components/CreditCardInput';
import { setStripeCallStatus, reinitialize } from 'reducers/creditCards';

type Props = {
  orgUuid: string,
  orgId: number,
  title: string,
  subtext: string,
  onRequestHide: () => void,
  createNewOrgCard: (stripeSourceId: string, orgUuid: string, orgId: number) => Promise<*>,
  actionText: string,

  // redux
  inputValid: boolean,
  errorMessage: ?string,
  stripeCallStatus: ?string,
  setStripeCallStatus: Function,
  sourceId: ?string,
  reinitialize: Function,
};

// Track the state of the thundercash call
type State = {
  submitting: boolean,
};

class AdminNewCardModal extends React.Component<Props, State> {
  state: State = {
    submitting: false,
  };

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.stripeCallStatus === 'done' && this.props.stripeCallStatus !== 'done') {
      this.handleCardCreationComplete(nextProps.sourceId);
    }
  }

  handleSubmit = (event: SyntheticEvent<>) => {
    event.preventDefault();
    this.setState({ submitting: true });
    this.props.setStripeCallStatus('start');
  };

  handleFinished = () => {
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }

    this.props.reinitialize();
  };

  handleCardCreationComplete = (sourceId: ?string) => {
    const onSuccess = () => {
      ToastActions.showSuccessToast('Card added successfully');
      this.setState({ submitting: false });
      this.handleFinished();
    };
    const onError = () => {
      this.setState({ submitting: false });
      ToastActions.showErrorToast(
        `
        There was an error adding your card. Please try again or email
        ${LinkConstants.SUPPORT_EMAIL}
        for help.
      `
      );

      this.props.reinitialize();
    };

    const { orgUuid, orgId, createNewOrgCard } = this.props;

    if (sourceId) {
      createNewOrgCard(sourceId, orgUuid, orgId)
        .then(({ data: { createNewOrgCard: resp } }) => {
          if (resp.error) {
            onError();
          } else {
            onSuccess();
          }
        })
        .catch(onError);
    } else {
      // There was an error in adding card to Stripe
      onError();
    }
  };

  render() {
    const { title, subtext, actionText, errorMessage, inputValid } = this.props;

    const { submitting } = this.state;

    return (
      <Dialog
        title={title}
        subtext={subtext}
        onRequestHide={this.handleFinished}
        className="AdminNewCardModal">
        <BoxBody>
          <form onSubmit={this.handleSubmit}>
            <Field error={errorMessage}>
              <CreditCardInput apiKey={Config.Environment.SAAS_STRIPE_KEY} />
            </Field>
            <Buttons>
              <Button level="secondary" onClick={this.handleFinished}>
                Cancel
              </Button>
              <Button type="submit" disabled={!inputValid} loading={submitting}>
                {actionText}
              </Button>
            </Buttons>
          </form>
        </BoxBody>
      </Dialog>
    );
  }
}

const withCreateNewOrgCard = graphql(
  gql`
    mutation createNewOrgCard($input: CreateNewOrgCardInput!) {
      createNewOrgCard(input: $input) {
        card {
          lastFour
          expiryMonth
          expiryYear
          zip
          brand
        }
        error {
          message
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      createNewOrgCard(stripeSourceId: string, orgUuid: string, orgId: number) {
        const input = { stripeSourceId, orgUuid, orgId };
        return mutate({
          variables: { input },
          update: (
            store,
            {
              data: {
                createNewOrgCard: { card, error },
              },
            }
          ) => {
            const currentData = store.readQuery({
              query: adminBillingOverviewQuery,
              variables: { organizationUuid: [orgUuid] },
            });

            if (error) {
              return;
            }

            currentData.organizations[0].billingCreditCard = card;

            store.writeQuery({ query: adminBillingOverviewQuery, data: currentData });
          },
        });
      },
    }),
  }
);

function mapStateToProps(state) {
  return {
    inputValid: state.creditCards.inputValid,
    errorMessage: state.creditCards.errorMessage,
    stripeCallStatus: state.creditCards.stripeCallStatus,
    sourceId: state.creditCards.sourceId,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators(
    {
      setStripeCallStatus,
      reinitialize,
    },
    dispatch
  );
}

export { AdminNewCardModal };
export default withCreateNewOrgCard(
  connect(mapStateToProps, mapDispatchToProps)(AdminNewCardModal)
);
